/**
 * crashTelemetry
 *
 * Single choke point for emitting crash analytics from the three renderer crash
 * listeners (ErrorBoundary, GlobalErrorListener, MainProcessCrashListener).
 * Before this existed each listener fired `app:crash-occurred` on EVERY
 * occurrence — so a single repeating crash flooded telemetry (805 fires from
 * one user, 318 from another). This routes each fire through
 * `crashCircuitBreaker`, so a storming signature is capped after
 * MAX_TELEMETRY_FIRES_PER_SIGNATURE and summarised with at most one
 * `app:crash-storm` per occurrence bucket.
 */

import { buildCrashOccurredPayload, signatureFor } from './crashDedupService';
import { recordTelemetryAttempt } from './crashCircuitBreaker';
import type {
  CrashOccurredArgs,
  CrashOccurredPayload,
  CrashStormBucket,
  CrashStormPayload,
} from '../types/crashTypes';

type TrackFn = (
  name: string,
  data?: Record<string, string | number | boolean | null>,
) => unknown;

/** Low-cardinality subset of the crash-occurred payload, for the storm summary. */
const buildStormPayload = (
  payload: CrashOccurredPayload,
  bucket: CrashStormBucket,
): CrashStormPayload => ({
  occurrenceBucket: bucket,
  category: payload.category,
  surface: payload.surface,
  source: payload.source,
  errorName: payload.errorName,
  messagePrefix: payload.messagePrefix,
});

/**
 * Build the `app:crash-occurred` payload, then fire it — and/or an
 * `app:crash-storm` summary — subject to the per-signature circuit breaker.
 * Fire-and-forget: `track` is optional so callers can hand in the (possibly
 * undefined) renderer analytics bridge without extra guards.
 */
export const emitCrashTelemetry = (track: TrackFn | undefined, args: CrashOccurredArgs): void => {
  const payload = buildCrashOccurredPayload(args);
  const { fireCrashOccurred, stormBucket } = recordTelemetryAttempt(
    signatureFor(args.context),
    payload.occurrenceCount,
  );

  if (fireCrashOccurred) {
    void track?.('app:crash-occurred', payload);
  }
  if (stormBucket) {
    void track?.('app:crash-storm', buildStormPayload(payload, stormBucket));
  }
};
